import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
    Loader2, Calendar, Phone, Mail, MapPin,
    CheckCircle2, AlertCircle, ClipboardList, BookOpen,
    X, ChevronRight,
} from "lucide-react";
import { useTutorAuth } from "../context/TutorAuthContext";
import { tutorApi, formatApiError } from "../lib/tutorApi";
import TutorMobileLayout, { MobileSkeletonCard } from "../components/tutor/TutorMobileLayout";

/**
 * V25 Phase 4/5 — Mobile-First My Demos & Ongoing Tuitions.
 */
export default function TutorDemos() {
    const { tutor } = useTutorAuth();
    const [search] = useSearchParams();
    const initialTab = search.get("tab") === "ongoing" ? "ongoing" : "active";
    const [tab, setTab] = useState(initialTab);
    const [demos, setDemos] = useState([]);
    const [ongoing, setOngoing] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);

    const reload = async () => {
        setLoading(true);
        try {
            const [{ data: d }, { data: o }] = await Promise.all([
                tutorApi.get("/tutor/me/demos"),
                tutorApi.get("/tutor/me/ongoing-tuitions"),
            ]);
            setDemos(d.items || []);
            setOngoing(o.items || []);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || err.message);
        } finally { setLoading(false); }
    };

    const handleAccept = async (demo) => {
        try {
            await tutorApi.post(`/tutor/me/demos/${demo.assignment_id}/accept`);
            toast.success("Demo accepted — schedule it now");
            reload();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || err.message);
        }
    };

    const handleReject = async (demo) => {
        // eslint-disable-next-line no-alert
        const reason = window.prompt("Decline this demo? Optionally tell admin why:", "");
        if (reason === null) return; // user cancelled
        try {
            await tutorApi.post(`/tutor/me/demos/${demo.assignment_id}/reject`, { reason });
            toast.success("Demo declined — admin will reassign");
            reload();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || err.message);
        }
    };

    useEffect(() => { if (tutor) reload(); }, [tutor]);

    if (!tutor) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

    const activeDemos = demos.filter((d) => ["assigned", "tutor_accepted", "demo_scheduled", "demo_done"].includes(d.assignment_status));
    const pastDemos = demos.filter((d) => !["assigned", "tutor_accepted", "demo_scheduled", "demo_done"].includes(d.assignment_status));

    return (
        <TutorMobileLayout title="My Demos">
            {/* Tabs */}
            <div className="flex gap-1.5 bg-white border border-slate-200 rounded-2xl p-1 mb-4" data-testid="tutor-demos-tabs">
                {[
                    { id: "active", label: "Active", count: activeDemos.length },
                    { id: "past", label: "Past", count: pastDemos.length },
                    { id: "ongoing", label: "Ongoing", count: ongoing.length },
                ].map((t) => (
                    <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tutor-demos-tab-${t.id}`}
                        className={`flex-1 py-2 rounded-xl text-xs font-bold transition ${tab === t.id ? "bg-[#1E3A8A] text-white shadow-sm" : "text-slate-500 hover:bg-slate-50"}`}>
                        {t.label}
                        <span className={`ml-1 text-[10px] px-1.5 rounded-full ${tab === t.id ? "bg-white/20" : "bg-slate-100 text-slate-600"}`}>{t.count}</span>
                    </button>
                ))}
            </div>

            {loading ? (
                <div className="space-y-3">{[...Array(3)].map((_, i) => <MobileSkeletonCard key={i} />)}</div>
            ) : tab === "active" ? (
                activeDemos.length === 0 ? (
                    <EmptyState icon={Calendar} title="No active demos" msg="When admin assigns you a tuition, it appears here with parent contact details." />
                ) : (
                    <div className="space-y-3">{activeDemos.map((d) => (
                        <DemoCard key={d.assignment_id} demo={d}
                            onAction={() => setSelected(d)}
                            onAccept={() => handleAccept(d)}
                            onReject={() => handleReject(d)} />
                    ))}</div>
                )
            ) : tab === "past" ? (
                pastDemos.length === 0 ? (
                    <EmptyState icon={ClipboardList} title="No past demos yet" msg="Your demo history will show up here." />
                ) : (
                    <div className="space-y-3">{pastDemos.map((d) => <DemoCard key={d.assignment_id} demo={d} readOnly />)}</div>
                )
            ) : (
                ongoing.length === 0 ? (
                    <EmptyState icon={BookOpen} title="No ongoing tuitions yet" msg="Approved tuitions activate here once the parent confirms after your demo." />
                ) : (
                    <div className="space-y-3">{ongoing.map((o) => <OngoingCard key={o.id} ongoing={o} />)}</div>
                )
            )}

            {selected && (
                <DemoActionModal demo={selected} onClose={() => setSelected(null)} onUpdated={() => { setSelected(null); reload(); }} />
            )}
        </TutorMobileLayout>
    );
}

function EmptyState({ icon: Icon, title, msg }) {
    return (
        <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-8 text-center">
            <Icon className="w-10 h-10 mx-auto text-slate-300 mb-2" />
            <div className="font-semibold text-slate-700">{title}</div>
            <div className="text-xs text-slate-500 mt-1">{msg}</div>
        </div>
    );
}

function DemoCard({ demo, onAction, onAccept, onReject, readOnly }) {
    const status = demo.assignment_status;
    const tone = {
        assigned: "bg-blue-100 text-blue-700",
        tutor_accepted: "bg-indigo-100 text-indigo-700",
        demo_scheduled: "bg-amber-100 text-amber-700",
        demo_done: "bg-emerald-100 text-emerald-700",
        demo_no_show: "bg-rose-100 text-rose-700",
        approved: "bg-emerald-100 text-emerald-700",
        rejected: "bg-rose-100 text-rose-700",
        tutor_rejected: "bg-rose-100 text-rose-700",
    }[status] || "bg-slate-100 text-slate-700";

    return (
        <article className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm" data-testid={`tutor-demo-card-${demo.assignment_id}`}>
            <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                    {(demo.student_name || "S").slice(0, 2).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${tone}`}>{status.replace(/_/g, " ")}</span>
                    </div>
                    <h3 className="font-bold text-[#0F172A] text-sm leading-tight mt-1 truncate">{demo.student_name || "—"}</h3>
                    <div className="text-[11px] text-slate-500">Class {demo.grade} · {demo.board} · {demo.subjects}</div>
                </div>
            </div>

            <div className="space-y-1 mt-3 text-[11px] text-slate-600">
                <a href={`tel:${demo.phone}`} className="flex items-center gap-1.5 hover:text-blue-700">
                    <Phone className="w-3 h-3 text-slate-400" /> {demo.phone || "—"}
                </a>
                {demo.email && <div className="flex items-center gap-1.5"><Mail className="w-3 h-3 text-slate-400" />{demo.email}</div>}
                <div className="flex items-start gap-1.5"><MapPin className="w-3 h-3 text-slate-400 mt-0.5 flex-shrink-0" />
                    <span>{[demo.address_house_no, demo.address_area, demo.city, demo.pin_code].filter(Boolean).join(", ")}</span>
                </div>
                {demo.parent_name && <div className="text-slate-500">Parent: <strong>{demo.parent_name}</strong></div>}
                {demo.preferred_demo_at && <div className="flex items-center gap-1.5"><Calendar className="w-3 h-3 text-slate-400" /> {demo.preferred_demo_at}</div>}
            </div>

            {demo.special_notes && (
                <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-2 text-[11px] text-amber-800">
                    <strong>Note:</strong> {demo.special_notes}
                </div>
            )}
            {demo.tutor_remarks && (
                <div className="mt-2 bg-slate-50 border border-slate-200 rounded-lg p-2 text-[11px] text-slate-700">
                    <strong>Your remarks:</strong> {demo.tutor_remarks}
                </div>
            )}
            {demo.parent_feedback?.decision && (
                <div className={`mt-2 rounded-lg p-2 text-[11px] border ${demo.parent_feedback.decision === "approve" ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-rose-50 text-rose-800 border-rose-200"}`}>
                    <strong>Parent:</strong> {demo.parent_feedback.decision.replace(/_/g, " ")}
                    {demo.parent_feedback.notes ? ` — ${demo.parent_feedback.notes}` : ""}
                </div>
            )}

            {!readOnly && status === "assigned" && (
                <div className="grid grid-cols-2 gap-2 mt-3">
                    <button onClick={onAccept} data-testid={`tutor-demo-accept-${demo.assignment_id}`}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold py-2.5 rounded-xl inline-flex items-center justify-center gap-1.5 active:scale-95 transition">
                        <CheckCircle2 className="w-3.5 h-3.5" /> Accept
                    </button>
                    <button onClick={onReject} data-testid={`tutor-demo-reject-${demo.assignment_id}`}
                        className="bg-white hover:bg-rose-50 border border-rose-300 text-rose-700 text-xs font-bold py-2.5 rounded-xl inline-flex items-center justify-center gap-1.5 active:scale-95 transition">
                        <X className="w-3.5 h-3.5" /> Decline
                    </button>
                </div>
            )}
            {!readOnly && ["tutor_accepted", "demo_scheduled"].includes(status) && (
                <button onClick={onAction} data-testid={`tutor-demo-action-${demo.assignment_id}`}
                    className="w-full mt-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white text-xs font-bold py-2.5 rounded-xl inline-flex items-center justify-center gap-1.5 active:scale-95 transition">
                    Update status
                </button>
            )}
            {!readOnly && status === "demo_done" && (
                <div className="mt-3 text-center text-[11px] text-slate-500 italic">Awaiting parent feedback</div>
            )}
        </article>
    );
}

function OngoingCard({ ongoing }) {
    return (
        <Link to={`/tutor/tuitions/${ongoing.id}/attendance`} data-testid={`tutor-ongoing-${ongoing.id}`}
            className="block bg-white border border-slate-200 rounded-2xl p-4 shadow-sm hover:shadow active:scale-[0.98] transition">
            <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                    {(ongoing.student_name || "S").slice(0, 2).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-[#0F172A] text-sm truncate">{ongoing.student_name}</h3>
                    <div className="text-[11px] text-slate-500">{ongoing.grade} · {ongoing.board} · {ongoing.subjects}</div>
                </div>
                <ChevronRight className="w-4 h-4 text-slate-400" />
            </div>
            <div className="text-[11px] text-slate-600 mt-2 space-y-0.5">
                <div>Schedule: <strong>{ongoing.schedule}</strong></div>
                <div>Fee: <strong>₹{ongoing.monthly_fee?.toLocaleString?.()} / {ongoing.fee_type || "monthly"}</strong></div>
            </div>
        </Link>
    );
}

function DemoActionModal({ demo, onClose, onUpdated }) {
    const [busy, setBusy] = useState(false);
    const [mode, setMode] = useState(["assigned", "tutor_accepted"].includes(demo.assignment_status) ? "schedule" : "done");
    const [scheduledAt, setScheduledAt] = useState("");
    const [remarks, setRemarks] = useState("");
    const [understanding, setUnderstanding] = useState("good");
    const [noShowWho, setNoShowWho] = useState("student");
    const [notes, setNotes] = useState("");

    const submit = async () => {
        setBusy(true);
        try {
            if (mode === "schedule") {
                if (!scheduledAt) return toast.error("Pick a date/time");
                await tutorApi.post(`/tutor/me/demos/${demo.assignment_id}/mark-scheduled`, { scheduled_at: scheduledAt, notes });
                toast.success("Demo scheduled");
            } else if (mode === "done") {
                if (remarks.length < 3) return toast.error("Please write a short remark");
                await tutorApi.post(`/tutor/me/demos/${demo.assignment_id}/mark-done`, { tutor_remarks: remarks, student_understanding: understanding });
                toast.success("Demo marked done");
            } else if (mode === "no_show") {
                await tutorApi.post(`/tutor/me/demos/${demo.assignment_id}/mark-no-show`, { who: noShowWho, notes });
                toast.success("Marked no-show");
            }
            onUpdated();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || err.message);
        } finally { setBusy(false); }
    };

    const inputCls = "w-full border border-slate-300 rounded-xl px-3 py-2 text-sm";

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end" data-testid="tutor-demo-action-modal">
            <div className="bg-white rounded-t-3xl w-full max-w-md max-h-[85vh] overflow-y-auto pb-6">
                <div className="sticky top-0 bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between z-10">
                    <h3 className="font-bold text-[#0F172A] truncate">{demo.student_name} · Class {demo.grade}</h3>
                    <button onClick={onClose} data-testid="tutor-demo-cancel-btn"><X className="w-5 h-5 text-slate-400" /></button>
                </div>
                <div className="px-4 py-4 space-y-3">
                    <div className="grid grid-cols-3 gap-2">
                        {[
                            { id: "schedule", label: "Schedule", icon: Calendar },
                            { id: "done", label: "Done", icon: CheckCircle2 },
                            { id: "no_show", label: "No-show", icon: AlertCircle },
                        ].map((b) => {
                            const Icon = b.icon;
                            return (
                                <button key={b.id} onClick={() => setMode(b.id)} data-testid={`tutor-demo-mode-${b.id}`}
                                    className={`py-2.5 rounded-xl text-xs font-bold inline-flex items-center justify-center gap-1 transition ${mode === b.id ? "bg-[#1E3A8A] text-white" : "bg-slate-100 text-slate-600"}`}>
                                    <Icon className="w-3.5 h-3.5" /> {b.label}
                                </button>
                            );
                        })}
                    </div>

                    {mode === "schedule" && (
                        <>
                            <label className="block"><span className="text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1 block">Date &amp; time</span>
                                <input type="datetime-local" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} data-testid="tutor-demo-schedule-at" className={inputCls} />
                            </label>
                            <label className="block"><span className="text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1 block">Notes (optional)</span>
                                <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} data-testid="tutor-demo-schedule-notes" className={inputCls} />
                            </label>
                        </>
                    )}

                    {mode === "done" && (
                        <>
                            <label className="block"><span className="text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1 block">Your remarks</span>
                                <textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} rows={4} data-testid="tutor-demo-done-remarks" className={inputCls} placeholder="How did the demo go?" />
                            </label>
                            <label className="block"><span className="text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1 block">Student understanding</span>
                                <select value={understanding} onChange={(e) => setUnderstanding(e.target.value)} data-testid="tutor-demo-done-understanding" className={inputCls}>
                                    <option value="excellent">Excellent</option>
                                    <option value="good">Good</option>
                                    <option value="average">Average</option>
                                    <option value="needs_work">Needs work</option>
                                </select>
                            </label>
                        </>
                    )}

                    {mode === "no_show" && (
                        <>
                            <label className="block"><span className="text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1 block">Who didn't show up?</span>
                                <select value={noShowWho} onChange={(e) => setNoShowWho(e.target.value)} data-testid="tutor-demo-noshow-who" className={inputCls}>
                                    <option value="student">Student</option>
                                    <option value="tutor">I couldn't make it</option>
                                    <option value="both">Both</option>
                                </select>
                            </label>
                            <label className="block"><span className="text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1 block">Notes (optional)</span>
                                <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} data-testid="tutor-demo-noshow-notes" className={inputCls} />
                            </label>
                        </>
                    )}

                    <button onClick={submit} disabled={busy} data-testid="tutor-demo-submit-btn"
                        className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white font-bold py-3 rounded-xl mt-2 disabled:opacity-60 inline-flex items-center justify-center gap-2 active:scale-[0.98] transition">
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Submit
                    </button>
                </div>
            </div>
        </div>
    );
}

/**
 * V25 Phase 4 — My Demos & Ongoing Tuitions for tutors.
 *  - Lists active + completed demos with full PII (parent name, phone, address).
 *  - Tutor can mark demo as scheduled, done (with remarks), or no-show.
 *  - Shows ongoing tuitions in a second card.
 */
